import { createSlice, type Draft, type PayloadAction, type SerializedError, type UnknownAction } from '@reduxjs/toolkit'
import { t } from 'i18next'
import type { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type {
  EnhancedConfigurationId,
  EnhancedLanguageId,
  EnhancedManCodeId,
  EnhancedMarketId,
  EnhancedProductFamilyId,
  EnhancedProductGroup,
  EnhancedProductGroupId,
  EnhancedProductVariantId,
  EnhancedVehicleTypeId,
  EnhancedViewId,
} from '#api/enhancements/enhancedTmsApiTypes'
import type { Brand } from '#api/enhancements/utils'
import type { EcmConfiguratorStatus } from '#api/tmsApiService'
import type { ProductPath } from '#app/types/AppTypes'
import { selectedFamilyVariant } from '#app/types/properties'
import { type ApiConfiguration, type FullApiConfiguration, isFullApiConfiguration } from '#features/Configuration/ApiConfiguration'
import type { ConfigurationStep } from '#features/Configuration/ConfigurationStep'
import { MarketDenominationPlaceholder, UntitledConfigurationPlaceholder } from '#features/Configuration/ConfigurationTitle'
import { makeSerializedError } from '#features/ErrorHandling/errorHelpers'
import type { Tg3ConversionInsightsLookup } from '#features/LegacyTg3Code/mapTg3ConversionInsights'
import { collectFamilySelectionMetaData } from '#features/NationalCodes/selectionHelpers'
import type { PrintOptions } from '#features/PrintOptions/PrintOptions'
import { translateBestEffort } from '#i18n/LocalizedString'
import type { AnyConfigurationViewId, ManCodeWithSortOrder } from '#pages/Configuration/components/ConfigurationNavigation'
import { tireGroupEntries } from '#pages/Configuration/TireEditor/tireIds'
import { getAllFamilies, getAllGroups } from '#pages/Configuration/utils/collections'
import { createInitialSelection } from '#pages/Configuration/utils/utils'
import type { StepErrorCause } from '#store/middlewares/configurationRequests/configurationStepListener'
import {
  type ConfigurationStage,
  makeRequestConfigurationStep,
  type RequestConfigurationStep,
  type SystemConfigurationStep,
  stageForView,
  stepForStage,
} from '#types/Configuration'
import type { CheckResultUpdate, CheckTaskResultsByEcmVersion } from '#types/ConfigurationCheck'
import { calculateTotalConfigurationPrice } from '#utils/calculateTotalConfigurationPrice'
import { deriveFinalStatus, isFullCheckResult } from '#utils/checkHelpers'
import { dateToString } from '#utils/DateString'
import {
  isOptionChangeSelectionId,
  isOptionChangeUndoId,
  makeOptionChangeId,
  makeOptionChangeRedoId,
  makeOptionChangeSelectionId,
  makeOptionChangeUndoId,
  type OptionChangeConsequence,
  type OptionChangeEntry,
  type OptionChangeId,
  type OptionChangeRedoQueued,
  type OptionChangeSelection,
  type OptionChangeSelectionQueued,
  type OptionChangeSucceeded,
  type OptionChangeUndoQueued,
} from '../builders/configChangeTracker'
import { addConflictErrorTracker } from '../builders/conflictErrorTracker'
import type { ConfigurationPlaceReason } from '../middlewares/configurationRequests/apiChangeListener'
import type {
  PendingConfigurationErrorCause,
  PendingConfigurationLoadingStep,
} from '../middlewares/configurationRequests/configurationCreationListener'
import type { ConfigurationTechnicalData } from '../middlewares/configurationRequests/configurationTechnicalDataListener'
import { type MarketData, marketDataReducers } from './configuration/marketSlice'

export interface ConfigurationProductFamily {
  selectedVariant?: { id: EnhancedProductVariantId; timestamp: Timestamp; previousSelection?: EnhancedProductVariantId }
  isLocked: boolean
  /** Print Options per variant */
  printOptions: Partial<Record<string, PrintOptions>>
}

export type SnapshotId = ReturnType<typeof crypto.randomUUID> & { __snapshotBrand: never }

/**
 * A snapshot of a configuration when a MAN code has been saved
 */
export interface ManCodeConfigurationSnapshot {
  id: SnapshotId
  reason: 'man-code'
  version: number
  when: Timestamp
  productGroups: TruckConfiguration['productGroups']
  initialSelection: TruckConfiguration['initialSelection']
  configuredFamilies: TruckConfiguration['configuredFamilies']
  view: AnyConfigurationViewId
}
/**
 * A snapshot of a configuration that is not necessarily saved, for example
 * when keeping a record of a configuration that just had been created
 */
export interface CurrentConfigurationSnapshot {
  id: SnapshotId
  reason: 'current-state'
  version: number
  when: Timestamp
  productGroups: TruckConfiguration['productGroups']
  initialSelection: TruckConfiguration['initialSelection']
  configuredFamilies: TruckConfiguration['configuredFamilies']
  view: AnyConfigurationViewId
}
export type ConfigurationSnapshot = ManCodeConfigurationSnapshot | CurrentConfigurationSnapshot

export interface ConfigurationStepDetails {
  /** Should always point to the over-arching step in the configuration */
  stage: ConfigurationStage
  view: AnyConfigurationViewId
  /** If present, the ID of the 2nd configuration that is being compared to */
  otherConfigurationId?: LocalConfigurationId
  otherConfigurationManCode?: ManCodeWithSortOrder
  /** Requested should always match the required system step of `view` without any additional requirements */
  requested: RequestConfigurationStep
  system: SystemConfigurationStep
  error?: StepErrorCause
  loading?: {
    to: 'DetailedStep' | 'GeneralStep'
    since: Timestamp
  }
}

export type ConfigurationVersion = Brand<number, '__ConfigurationVersion'>
export type EcmConfigurationVersion = Brand<number, '__EcmConfigurationVersion'>

export interface TruckConfiguration {
  id: LocalConfigurationId
  uuid?: EnhancedConfigurationId
  // configuration version, incremented on any change
  version: ConfigurationVersion
  // configuration change version, incremented whenever the ecm configuration is changed in the backend
  ecmVersion: EcmConfigurationVersion
  sortOrder: number
  step: ConfigurationStepDetails
  title: string
  type: 'truck-configuration'
  lastEcmState?: EcmConfiguratorStatus
  marketDenomination?: string
  lastApiInteraction: Timestamp
  lastChange?: { when: Timestamp; version: number }
  lastManCodeUpdate?: { when?: Timestamp; version?: number; currentlySaving?: boolean }
  snapshots: ConfigurationSnapshot[]
  // the api configuration that was used to create this configuration
  callingApiConfiguration: ApiConfiguration
  // the full api configuration that is used to load the configuration, in the case of a man code this will be resolved when retrieving the man code
  apiConfiguration: FullApiConfiguration
  // raw configuration data
  productGroups: EnhancedProductGroup[]
  compatibility?: {
    version: number
    focusGroup: EnhancedProductGroupId
    /** All the groups that are affected by the lookup of the focus group */
    requestedProductGroups: EnhancedProductGroupId[]
    /** All the groups that have been checked by checking the focus group */
    checkedProductGroups: EnhancedProductGroupId[]
  }
  // semi-hostile: the base data is stored in the caches and not visible here
  configuredFamilies: Partial<Record<EnhancedProductFamilyId, ConfigurationProductFamily>>
  history?: OptionChangeEntry[]
  initialSelection?: Record<EnhancedProductFamilyId, EnhancedProductVariantId>
  viewHighlight: Partial<Record<TruckConfigurationHighlight['scope'], TruckConfigurationHighlight>>
  viewDetails?: { details?: ViewDetails; print?: ViewDetails; changes?: ViewDetails; history?: ViewDetails }
  sidepanel: { current: 'summary' | 'cosima' | 'history' | 'delivery'; previous: 'summary' | 'cosima' | 'history' | 'delivery' }
  checks: CheckTaskResultsByEcmVersion
  technicalData?: { version: number } & ({ status: 'requested' | 'error' } | { status: 'loaded'; data: ConfigurationTechnicalData })
  showCava?: boolean
  optionalEquipment: ConfigurationOptionalEquipment[]
  creation: {
    createdAt: Timestamp
    loadingStep: PendingConfigurationLoadingStep
    error?: {
      loadingErrorCause: PendingConfigurationErrorCause
      loadingStep?: PendingConfigurationLoadingStep
    } & SerializedError
  }
  tg3?: {
    tg3TargetApiConfiguration?: FullApiConfiguration
    tg3ConversionInsightsLookup?: Tg3ConversionInsightsLookup
    appliedConversion?: ConfigurationStep[]
  }
}

export type ViewDetailKey = keyof Exclude<TruckConfiguration['viewDetails'], undefined>

export interface ViewDetails {
  scrollPosition?: {
    path: ProductPath
    source: string
  }
  // storing collapsed groups to stay collapsed when returning to a view
  collapsedGroups: EnhancedProductGroupId[]
}

export interface TruckConfigurationHighlight {
  /** What is being highlighted? */
  familyId: EnhancedProductFamilyId
  /** What is being highlighted? */
  variantId: EnhancedProductVariantId
  /** Where is it visible? */
  scope: 'panel' | 'modal'
  /** Which step did the family highlight happen? (so it does not carry over by accident) */
  stage: ConfigurationStage
  /** Should other options be shown in the modal? */
  displayMode?: 'read-only' | 'selectable'
}

export interface TruckConfigurationMetaData {
  hiddenGroups: EnhancedProductGroupId[]
  sessionExpiryInMs: number
  // time when the session should be refreshed
  sessionGracePeriodInMs: number
  // time when the user should be warned about the session expiry
  sessionWarningPeriodInMs: number
}

export interface ConfigurationChangeRequest {
  id: LocalConfigurationId
  selections: { familyId: EnhancedProductFamilyId; variantId: EnhancedProductVariantId }[]
}

export interface OperationalConfigurationChange {
  type: 'operational'
  enqueued: ConfigurationChangeRequest[]
  current?: ConfigurationChangeRequest
}

export type ConfigurationChangeApiRequest = Awaited<
  ReturnType<ReturnType<(typeof enhancedTmsApiService)['endpoints']['postApiConfigurationByUuidStepAndStep']['initiate']>>
>

export interface FailedConfigurationChange {
  type: 'failure'
  enqueued: ConfigurationChangeRequest[]
  cause: {
    proposal: ConfigurationChangeRequest
    request: ConfigurationChangeApiRequest
    process: OptionChangeId
  }
}

export interface OptionChangeMetaData {
  state: OperationalConfigurationChange | FailedConfigurationChange
  history: ConfigurationChangeRequest[]
}

export type ConfigurationOptionalEquipmentId = Brand<`optional-equipment/${ReturnType<typeof crypto.randomUUID>}`, '__optionalEquipmentId'>
export interface ConfigurationOptionalEquipment {
  id: ConfigurationOptionalEquipmentId
  title: string
  description?: string
  variants: {
    family: { id: EnhancedProductFamilyId; title: string }
    sourceVariant?: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    targetVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    version: number
  }[]
  implicitChanges: {
    family: { id: EnhancedProductFamilyId; title: string }
    sourceVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    targetVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    version: number
  }[]
  /**
   * Determines the state of the offering, meaning:
   * - removed: has been removed, will be ignored (currently keeping it, may as well)
   * - unchecked:
   */
  status: 'removed' | 'unchecked' | 'in-progress' | 'checked' | 'offered' | 'withdrawn'
  checkVersion?: number
}

export type RecoverableConfiguration = Omit<
  TruckConfiguration,
  // omitting anything that is either being loaded or semi-irrelevant
  'step' | 'type' | 'productGroups' | 'checks'
> & { type: 'recoverable-configuration'; step: { view: TruckConfiguration['step']['view'] } }

export type LocalConfigurationId = Brand<`MAIC-${string}`, '__localConfigurationId'>

export interface TruckConfigurations {
  // pendingConfigurations: Partial<Record<PendingConfigurationId, TruckConfiguration>>
  configurations: Partial<Record<LocalConfigurationId, TruckConfiguration>>
  // these magically appear in the `appLocalStorage.tsx` serializer: Whenever the app stores the state, opened configs are serialized as such
  recoverableConfigurations: RecoverableConfiguration[]
  closedConfigurations: LocalConfigurationId[]
  leadingConfiguration: LocalConfigurationId | undefined
  meta: TruckConfigurationMetaData
  // optionChanges: OptionChangeMetaData
  apiConfiguration: FullApiConfiguration
  marketData: MarketData
}

const initialTruckConfigurationState: TruckConfigurations = {
  configurations: {},
  recoverableConfigurations: [],
  closedConfigurations: [],
  leadingConfiguration: undefined,
  meta: {
    /**
     * 2024-09-24:
     * These are groups that should usually be hidden from the user and are part of the option selection process (maybe?)
     * It is likely that they're part of some admin tool configuration and are currently placed hardcoded in here.
     * These are written down here to filter them from the views. `Affecting` and `Complemented` are both primary groups
     * **without** subgroups, but have families and options inside of them. This could be an alternative way of filtering
     * these out.
     */
    hiddenGroups: [
      'Info',
      'RAR_tyre_keeper',
      'Affecting',
      'Complemented',
      'AboutGroup',
      'Solution Proposal',
      // see: makeGroupNotPartOfWellKnownOperator @ useFilteredProductGroups.ts
      // currently hardcoded approach to filter these view internal groups
      // '8152-NonViewOspecGroup',
      // '8177-NonViewOspecGroup',
      // '8193-NonViewOspecGroup',
    ] as EnhancedProductGroupId[],
    /**
     * 2025-01-24:
     * The session expiry is the time it takes a session until it becomes invalid in the temporary configuration systems.
     * This value should be controlled from some endpoint or data entry that does not exist yet.
     * However, currently the expiry is very much fixed to an hour, which we are using for countdowns, expiry timers and
     * such.
     */
    sessionExpiryInMs: 60 * 60 * 1000,
    sessionGracePeriodInMs: 5 * 60 * 1000,
    sessionWarningPeriodInMs: 2 * 60 * 1000,
  },
  apiConfiguration: {
    market: -1 as EnhancedMarketId,
    vehicleType: -1 as EnhancedVehicleTypeId,
    view: -1 as EnhancedViewId,
    language: -1 as EnhancedLanguageId,
    partPeriod: dateToString(new Date()),
  },
  marketData: {},
}

export const createLocalConfigurationId = (): LocalConfigurationId => `MAIC-${Date.now().toString(36).toUpperCase()}` as LocalConfigurationId
export const createOptionalEquipmentId = (): ConfigurationOptionalEquipmentId =>
  `optional-equipment/${crypto.randomUUID()}` as ConfigurationOptionalEquipmentId

const updateVersion = (configuration: Draft<TruckConfiguration>, wasEcmChange: boolean) => {
  configuration.version = (configuration.version + 1) as ConfigurationVersion
  if (wasEcmChange) {
    configuration.ecmVersion = (configuration.ecmVersion + 1) as EcmConfigurationVersion
  }
  configuration.lastChange = {
    when: Date.now(),
    version: configuration.version,
  }
}

/**
 * If a request already did check for the compatibility of a group by setting `groupForCompatibilityCheck`
 * (for example APIs like `/change_group_of_interest`, `/select-variant`)
 * or manual checking has occurred, then track the groups that are affected by the compatibility check
 */
const trackVariantCompatibility = (
  configuration: Draft<TruckConfiguration>,
  version: ConfigurationVersion,
  checkedProductGroup: EnhancedProductGroupId | undefined,
) => {
  if (!checkedProductGroup) {
    return
  }
  // get all subgroups, they're now checked as well
  const allGroups = getAllGroups(configuration.productGroups)
  const group = allGroups.find((x) => x.id === checkedProductGroup)
  const allSubGroups = group && getAllGroups(group)
  if (!allSubGroups) {
    return
  }
  const requestedProductGroups = configuration.compatibility?.requestedProductGroups ?? []
  const checkedProductGroups = configuration.compatibility?.checkedProductGroups ?? []
  for (const subGroup of allSubGroups) {
    if (!requestedProductGroups.includes(subGroup.id)) {
      requestedProductGroups.push(subGroup.id)
    }
    if (!checkedProductGroups.includes(subGroup.id)) {
      checkedProductGroups.push(subGroup.id)
    }
  }

  configuration.compatibility = {
    version,
    focusGroup: checkedProductGroup,
    // this needs to be a new object identity for immer to catch the update correctly
    requestedProductGroups: [...requestedProductGroups],
    checkedProductGroups: [...checkedProductGroups],
  }
}

export const createPendingTruckConfiguration = (
  id: LocalConfigurationId,
  callingApiConfiguration: ApiConfiguration,
  prefilledConfigurationData: Partial<TruckConfiguration> | undefined,
  order: number,
): TruckConfiguration => ({
  id,
  callingApiConfiguration,
  apiConfiguration: isFullApiConfiguration(callingApiConfiguration)
    ? callingApiConfiguration
    : // TODO this is a temporary hack to prevent adapting all places where the apiConfiguration is used
      ({
        ...callingApiConfiguration,
      } as FullApiConfiguration),
  sortOrder: order,
  uuid: undefined,
  version: 0 as ConfigurationVersion,
  ecmVersion: 0 as EcmConfigurationVersion,
  step: {
    stage: 'PendingStep',
    view: 'configuration/new',
    requested: makeRequestConfigurationStep('GeneralStep'),
    system: 'unknown',
    loading: undefined,
  },
  title: UntitledConfigurationPlaceholder,
  type: 'truck-configuration',
  marketDenomination: MarketDenominationPlaceholder,
  lastApiInteraction: Date.now(),
  snapshots: [],
  productGroups: [],
  configuredFamilies: {},
  viewHighlight: {},
  sidepanel: { current: 'summary', previous: 'summary' },
  checks: {},
  optionalEquipment: [],
  viewDetails: {
    details: { collapsedGroups: [tireGroupEntries.id] },
  },
  ...prefilledConfigurationData,
  creation: {
    createdAt: Date.now(),
    loadingStep: 'pending',
    error: undefined,
  },
})

export const truckConfigurationSlice = createSlice({
  name: 'truck-configurations',
  initialState: initialTruckConfigurationState,
  reducers: {
    addPendingConfiguration: (
      state,
      {
        payload: { id, callingApiConfiguration, prefilledConfigurationData },
      }: PayloadAction<{
        id: LocalConfigurationId
        callingApiConfiguration: ApiConfiguration
        creation?: Timestamp
        prefilledConfigurationData?: Partial<TruckConfiguration>
      }>,
    ) => {
      // disallow creation of configurations that have been closed prior
      if (state.closedConfigurations.includes(id)) {
        return
      }
      const maxOrder =
        Object.values(state.configurations)
          .map((x) => x?.sortOrder)
          .reduce((a, b) => Math.max(a ?? 0, b ?? 0), 0) ?? 0
      state.configurations[id] = createPendingTruckConfiguration(id, callingApiConfiguration, prefilledConfigurationData, maxOrder + 1)
      if (state.leadingConfiguration === undefined || state.configurations[state.leadingConfiguration] === undefined) {
        state.leadingConfiguration = id
      }
    },
    _updatePendingConfiguration: (
      state,
      {
        payload: { uuid, id, creation },
      }: PayloadAction<{
        uuid: EnhancedConfigurationId | undefined
        id: LocalConfigurationId | undefined
        creation: Partial<TruckConfiguration['creation']>
      }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      if (uuid) {
        configuration.uuid = uuid
      }

      configuration.creation = {
        ...configuration.creation,
        ...creation,
        error: creation.error
          ? {
              ...creation.error,
              // store the step that was loading when the error occurred
              loadingStep: configuration.creation.loadingStep,
            }
          : undefined,
      }
    },
    createNewConfiguration: (state, { payload }: PayloadAction<TruckConfiguration>) => {
      // disallow creation of configurations that have been closed prior
      if (state.closedConfigurations.includes(payload.id)) {
        return
      }
      payload.initialSelection = createInitialSelection(payload)
      const maxOrder =
        Object.values(state.configurations)
          .map((x) => x?.sortOrder)
          .reduce((a, b) => Math.max(a ?? 0, b ?? 0), 0) ?? 0

      for (const config of Object.values(state.configurations)) {
        if (config && config.sortOrder === payload.sortOrder && config.id !== payload.id) {
          payload.sortOrder = maxOrder + 1
        }
      }
      state.configurations[payload.id] = payload
      if (state.leadingConfiguration === undefined || state.configurations[state.leadingConfiguration] === undefined) {
        state.leadingConfiguration = payload.id
      }
    },
    /** basically like createNewConfiguration, but without the initial selection  */
    _placeConfiguration: (
      state,
      { payload: { configuration } }: PayloadAction<{ configuration: TruckConfiguration; reason: ConfigurationPlaceReason }>,
    ) => {
      // disallow placements when the id has been closed prior
      if (state.closedConfigurations.includes(configuration.id)) {
        return
      }
      state.configurations[configuration.id] = configuration
      if (state.leadingConfiguration === undefined || state.configurations[state.leadingConfiguration] === undefined) {
        state.leadingConfiguration = configuration.id
      }
    },
    applyConfigurationBaseParameterChange: (
      state,
      { payload }: PayloadAction<{ id: LocalConfigurationId; overrideConfiguration: Partial<TruckConfiguration> }>,
    ) => {
      const configuration = state.configurations[payload.id]
      if (!configuration) {
        throw new Error(`Configuration ${payload.id} not found`)
      }
      const forbiddenKeys = new Set(['id'])

      // apply all overrideConfiguration properties to the configuration
      for (const [key, value] of Object.entries(payload.overrideConfiguration)) {
        if (key in configuration && !forbiddenKeys.has(key)) {
          // @ts-expect-error
          configuration[key] = value
        }
      }
    },
    setView: (
      state,
      {
        payload: { id, view, otherConfigurationId, otherConfigurationManCode },
      }: PayloadAction<{
        id: LocalConfigurationId | undefined
        view: AnyConfigurationViewId
        otherConfigurationId?: LocalConfigurationId
        otherConfigurationManCode?: ManCodeWithSortOrder
      }>,
    ) => {
      // edge case: copy is a synthetic state
      if (view === 'configuration/copy') {
        return
      }
      const config = id && state.configurations[id]
      const stage = stageForView(view)
      if (config && stage) {
        config.step = {
          ...config.step,
          stage,
          view,
          otherConfigurationId,
          otherConfigurationManCode,
          requested: view !== 'configuration/error' ? stepForStage(stage) : config.step.requested,
        }
      }
    },
    _placeStep: (
      state,
      {
        payload: { id, step },
      }: PayloadAction<{
        id: LocalConfigurationId | undefined
        step: Partial<Pick<TruckConfiguration['step'], 'system' | 'loading' | 'error' | 'requested'>>
      }>,
    ) => {
      const config = id && state.configurations[id]
      if (config) {
        config.step = {
          ...config.step,
          error: step.error ? step.error : undefined,
          requested: step.requested ?? config.step.requested,
          system: step.system ?? config.step.system,
          loading: step.loading ?? config.step.loading,
        }
      }
    },
    _setEcmState: (
      state,
      { payload: { id, ecmState } }: PayloadAction<{ id: LocalConfigurationId | undefined; ecmState: EcmConfiguratorStatus | undefined }>,
    ) => {
      const config = id && state.configurations[id]
      if (config) {
        config.lastEcmState = ecmState
      }
    },
    _randomizePrices: (state, { payload: { id } }: PayloadAction<{ id: LocalConfigurationId | undefined }>) => {
      const config = id && state.configurations[id]
      if (config) {
        for (const group of config.productGroups) {
          if (group.subGroups) {
            for (const subGroup of group.subGroups) {
              if (subGroup.families) {
                for (const family of subGroup.families) {
                  for (const variant of family.variants) {
                    if (variant.dummy_price) {
                      variant.dummy_price = variant.dummy_price + Math.max(-variant.dummy_price + 1, Math.floor(Math.random() * 1000) - 500)
                    }
                  }
                }
              }
            }
          }

          if (group.families) {
            for (const family of group.families) {
              for (const variant of family.variants) {
                if (variant.dummy_price) {
                  variant.dummy_price = variant.dummy_price + Math.max(-variant.dummy_price + 1, Math.floor(Math.random() * 1000) - 500)
                }
              }
            }
          }
        }
      }
    },
    setVariantForNationalCodeFamily: (
      state,
      action: PayloadAction<{
        configurationId?: LocalConfigurationId
        familyId?: EnhancedProductFamilyId
        toVariantId?: EnhancedProductVariantId
      }>,
    ) => {
      const {
        payload: { configurationId, familyId, toVariantId: variantId },
      } = action
      if (!configurationId || !(configurationId in state.configurations) || !familyId) {
        return
      }
      const configuration = state.configurations[configurationId]
      if (!configuration) {
        return
      }

      const configuredFamily = configuration.configuredFamilies[familyId] ?? { printOptions: {}, isLocked: false }
      if (configuredFamily.selectedVariant?.id === variantId) {
        return
      }

      const marketDetails = state.marketData[configuration.apiConfiguration.market]?.details
      const selectionInfo = collectFamilySelectionMetaData(marketDetails, familyId, configuredFamily.selectedVariant?.id, variantId)

      if (!selectionInfo) {
        console.warn(`ctx: setVariantForFamily: family ${familyId} not found in configuration ${configurationId}`)
        return
      }

      if (!selectionInfo.targetVariant) {
        console.warn(
          `ctx: setVariantForFamily: variant ${variantId} not found in family ${translateBestEffort(selectionInfo.family.label)} (${familyId})`,
        )
        return
      }

      // only overwrite the sidepanel if it's not showing the history, to avoid `previous` being duplicated as both being `history`
      if (configuration.sidepanel.current !== 'history') {
        configuration.sidepanel = { current: 'history', previous: configuration.sidepanel.current }
      }

      updateVersion(configuration, false)

      const previousUserSelection = variantId && configuration.configuredFamilies[familyId]?.selectedVariant?.id

      const wasUserSelectedVariant = configuredFamily.selectedVariant?.id === selectionInfo.previousVariant?.codeId
      configuration.configuredFamilies[familyId] = {
        ...configuredFamily,
        isLocked: !!variantId,
        selectedVariant: variantId ? { id: variantId, timestamp: Date.now(), previousSelection: previousUserSelection } : undefined,
      }

      const { family, targetVariant, previousVariant } = selectionInfo

      const priceDelta = (targetVariant.attributes?.grossListPrice ?? 0) - (previousVariant?.attributes?.grossListPrice ?? 0)
      const priceSum = calculateTotalConfigurationPrice(configuration.productGroups, marketDetails, configuration.configuredFamilies)

      configuration.history = [
        ...(configuration.history || []),
        {
          status: 'success',
          process: makeOptionChangeSelectionId(),
          started: Date.now(),
          ended: Date.now(),
          config: configuration.id,
          type: 'change',
          optionSource: 'national-code',
          selections: [
            {
              family: {
                id: familyId,
                title: translateBestEffort(family.label) ?? t('configuration.changes.labels.unknown-family', 'Unknown Family'),
              },
              fromVariant: {
                id: (previousVariant?.executionId ?? '-') as EnhancedProductVariantId,
                title: translateBestEffort(previousVariant?.label) ?? t('configuration.changes.labels.unknown-variant', 'Unknown Variant'),
                price: previousVariant?.attributes?.grossListPrice ?? 0,
              },
              toVariant: {
                id: (targetVariant?.executionId ?? '-') as EnhancedProductVariantId,
                title: translateBestEffort(targetVariant?.label) ?? t('configuration.changes.labels.unknown-variant', 'Unknown Variant'),
                price: targetVariant?.attributes?.grossListPrice ?? 0,
              },
              wasUserSelectedVariant,
            },
          ],
          deselections: [],
          conflicts: [],
          consequences: {
            priceDelta,
            priceSum,
            additions: [],
            changes: [],
            removals: [],
          },
        } satisfies OptionChangeSucceeded,
      ]
    },
    setVariantsForFamily: (
      state,
      action: PayloadAction<{
        configurationId: LocalConfigurationId | undefined
        selections?: {
          familyId?: EnhancedProductFamilyId
          toVariantId?: EnhancedProductVariantId
        }[]
        deselections?: {
          familyId?: EnhancedProductFamilyId
          fromVariantId?: EnhancedProductVariantId
        }[]
      }>,
    ) => {
      const {
        payload: { configurationId, selections: externalSelections, deselections: externalDeselections },
      } = action
      const totalSelections = (externalSelections?.length ?? 0) + (externalDeselections?.length ?? 0)
      const configuration = configurationId && state.configurations[configurationId]
      if (!configuration || totalSelections <= 0) {
        return
      }
      const families = getAllFamilies(configuration.productGroups)

      const selections = externalSelections ?? []
      const familiesFromSelection = families.filter((x) => selections.some((y) => y.familyId === x?.id))
      const newSelections: OptionChangeSelection['selections'] = []

      for (const { familyId, toVariantId: variantId } of selections) {
        if (!familyId) {
          continue
        }
        const family = familiesFromSelection.find((x) => x && x.id === familyId)

        // cannot select a family that does not exist in the configuration
        if (!family) {
          console.warn(`ctx: setVariantForFamily: family ${familyId} not found in configuration ${configurationId}`)
          continue
        }

        const toVariant = family.variants.find((x) => x.id === variantId)
        // cannot change to a variant that's not existing in the family
        if (!toVariant) {
          console.warn(`ctx: setVariantForFamily: variant ${variantId} not found in family ${familyId}`)
          continue
        }

        const configuredFamily = configuration.configuredFamilies[familyId] ?? { printOptions: {}, isLocked: false }
        // if its been already selected _and_ the API knows about it
        if (configuredFamily.selectedVariant?.id === variantId && toVariant?.isSelected) {
          continue
        }

        // while this is tempting, the API marks some families as explicit, if another selection has a direct dependency on this family
        // therefore some selections are explicit, but "not really":tm:
        // if(family.selectionType === 'Explicit' && toVariant.isSelected)

        // get the previous variant before overwriting the selection
        const previousVariant = selectedFamilyVariant(family, configuredFamily) ?? family.variants[0]
        const wasUserSelectedVariant = configuredFamily.selectedVariant?.id === previousVariant.id

        // a re-selection from no variant is allowed
        configuredFamily.selectedVariant = variantId ? { id: variantId, timestamp: Date.now(), previousSelection: previousVariant.id } : undefined
        configuredFamily.isLocked = true
        configuration.configuredFamilies = { ...configuration.configuredFamilies, [familyId]: configuredFamily }

        const fromVariant: OptionChangeSelectionQueued['selections'][number]['fromVariant'] = {
          id: previousVariant.id,
          title: previousVariant.title,
          price: previousVariant.dummy_price ?? -1,
        }
        // console.log(`ctx: setVariantForFamily: ${fromVariant.id}`)
        // enqueue new history entry (oldest -> most recent)
        newSelections.push({
          family: {
            id: family.id,
            title: family.title,
          },
          fromVariant: fromVariant,
          wasUserSelectedVariant,
          toVariant: {
            id: toVariant.id,
            title: toVariant.title,
            price: toVariant.dummy_price ?? -1,
          },
        })
      }

      const deselections = externalDeselections ?? []
      const familiesFromDeselection = families.filter((x) => deselections.some((y) => y.familyId === x?.id))
      const newDeselections: OptionChangeSelection['deselections'] = []
      for (const { familyId, fromVariantId: variantId } of deselections) {
        if (!familyId) {
          continue
        }
        const family = familiesFromDeselection.find((x) => x && x.id === familyId)
        if (!family) {
          console.warn(`ctx: setVariantForFamily: family ${familyId} not found in configuration ${configurationId}`)
          continue
        }

        const fromVariant = family.variants.find((x) => x.id === variantId)
        // cannot change to a variant that's not existing in the family
        if (!fromVariant) {
          console.warn(`ctx: setVariantForFamily: variant ${variantId} not found in family ${familyId}`)
          continue
        }
        const configuredFamily = configuration.configuredFamilies[familyId] ?? { printOptions: {}, isLocked: false }
        configuration.configuredFamilies[familyId] = { ...configuredFamily, selectedVariant: undefined, isLocked: false }
        newDeselections.push({
          family: {
            id: family.id,
            title: family.title,
          },
          fromVariant: {
            id: fromVariant.id,
            title: fromVariant.title,
            price: fromVariant.dummy_price ?? -1,
          },
          wasUserSelectedVariant: configuredFamily.selectedVariant !== undefined,
        })
      }

      // nothing to change for the API
      if (newSelections.length <= 0 && newDeselections.length <= 0) {
        return
      }

      configuration.history = [
        ...(configuration.history?.filter((x) => x.type === 'change' || (x.status !== 'pending' && x.status !== 'queued')) ?? []),
        {
          process: makeOptionChangeSelectionId(),
          started: Date.now(),
          config: configuration.id,
          status: 'queued',
          type: 'change',
          optionSource: 'ecm',
          selections: newSelections,
          deselections: newDeselections,
        },
      ]
    },
    setIsManCodeSaving: (state, { payload: { id, isSaving } }: PayloadAction<{ id: LocalConfigurationId | undefined; isSaving: boolean }>) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      configuration.lastManCodeUpdate = { ...configuration.lastManCodeUpdate, currentlySaving: isSaving }
    },
    setManCode: (
      state,
      {
        payload: { id, manCode, version, manUpdateTimestamp },
      }: PayloadAction<{
        id: LocalConfigurationId
        version: number
        manCode: EnhancedManCodeId
        manUpdateTimestamp?: Timestamp
      }>,
    ) => {
      const config = id && state.configurations[id]
      if (!config) {
        return
      }

      config.apiConfiguration.manCode = manCode
      config.lastManCodeUpdate = {
        version,
        when: manUpdateTimestamp ?? Date.now(),
        currentlySaving: false,
      }
    },
    setApiMode: (
      state,
      { payload: { id, mode } }: PayloadAction<{ id: LocalConfigurationId | undefined; mode: TruckConfiguration['apiConfiguration']['mode'] }>,
    ) => {
      const config = id && state.configurations[id]
      if (!config) {
        return
      }

      config.apiConfiguration.mode = mode
      // @TODO: suspicious, the other reducers don't look like that
      state.configurations[id] = config
    },
    setPrintOptions: (
      state,
      {
        payload: {
          id,
          options: { family, variant, printOptions },
        },
      }: PayloadAction<{
        id: LocalConfigurationId | undefined
        options: { family: EnhancedProductFamilyId | undefined; variant: EnhancedProductVariantId | undefined; printOptions: PrintOptions }
      }>,
    ) => {
      const config = id && state.configurations[id]
      if (!config || !family || !variant) {
        return
      }
      const configuredFamily = config.configuredFamilies[family] ?? { printOptions: {}, isLocked: false }
      configuredFamily.printOptions[variant] = printOptions
      config.configuredFamilies[family] = configuredFamily
      config.lastChange = {
        when: Date.now(),
        version: config.version,
      }
    },
    setConfigurationTitle: (state, { payload: { id, title } }: PayloadAction<{ id: LocalConfigurationId | undefined; title: string }>) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }

      configuration.title = title
      updateVersion(configuration, false)
    },
    closeConfiguration: (state, { payload }: PayloadAction<LocalConfigurationId | undefined>) => {
      if (!payload) {
        return
      }
      delete state.configurations[payload]
      state.recoverableConfigurations = state.recoverableConfigurations.filter((x) => x.id !== payload)
      state.closedConfigurations = [...new Set([...state.closedConfigurations, payload])]
      if (state.leadingConfiguration === payload) {
        state.leadingConfiguration = Object.values(state.configurations)
          .toSorted((a, b) => (a?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b?.sortOrder ?? Number.MAX_SAFE_INTEGER))
          ?.at(0)?.id
      }
    },
    toggleLocked: (state, { payload: { family, id } }: PayloadAction<{ family: EnhancedProductFamilyId; id: LocalConfigurationId | undefined }>) => {
      const config = id && state.configurations[id]
      if (!config) {
        return
      }
      const configuredFamily = config.configuredFamilies[family] ?? { printOptions: {}, isLocked: false }
      configuredFamily.isLocked = !configuredFamily.isLocked
      config.configuredFamilies[family] = configuredFamily
      config.lastChange = {
        when: Date.now(),
        version: config.version,
      }
    },
    _processConfigurationChange: (
      state,
      {
        payload,
      }: PayloadAction<
        ConfigurationChangeRequest & {
          process: OptionChangeId
          request: {
            id: string
            started: Timestamp
          }
        }
      >,
    ) => {
      const configuration = state.configurations[payload.id]
      const history = configuration?.history
      if (!history || history.length <= 0) {
        return
      }
      // disallow to process the history if there are unresolved failures in it
      if (history.some((x) => x.status === 'failed-unresolved')) {
        return
      }
      // reject processing if there are still requests in flight
      if (history.some((x) => x.status === 'pending')) {
        return
      }

      const changeRequestIdx = history.findIndex((x) => x.process === payload.process)
      // change request has not been found
      if (changeRequestIdx < 0) {
        return
      }
      const changeRequest = history[changeRequestIdx]
      // only allowed to process it if it's queued
      if (changeRequest.status !== 'queued') {
        return
      }

      history[changeRequestIdx] = {
        ...changeRequest,
        status: 'pending',
        request: { ...payload.request },
      }
    },
    _succeedConfigurationChange: (
      state,
      {
        payload,
      }: PayloadAction<
        ConfigurationChangeRequest & {
          process: OptionChangeId
          conflicts: OptionChangeSucceeded['conflicts']
          consequences: OptionChangeConsequence
          checkedCompatibilityGroup?: EnhancedProductGroupId
        }
      >,
    ) => {
      const configuration = state.configurations[payload.id]
      const history = configuration?.history
      if (!history || history.length <= 0) {
        return
      }
      // disallow to process the history if there are unresolved failures in it
      if (history.some((x) => x.status === 'failed-unresolved')) {
        return
      }

      const changeRequestIdx = history.findIndex((x) => x.process === payload.process)
      // change request has not been found
      if (changeRequestIdx < 0) {
        return
      }
      const changeRequest = history[changeRequestIdx]
      // disallow non-pending successor messages
      if (changeRequest.status !== 'pending') {
        return
      }

      updateVersion(configuration, true)
      // this will not mutate the last focus group checking if the compat group is undefined
      trackVariantCompatibility(configuration, configuration.version, payload.checkedCompatibilityGroup)

      history[changeRequestIdx] = {
        ...changeRequest,
        status: 'success',
        ended: Date.now(),
        conflicts: payload.conflicts,
        consequences: payload.consequences,
      }

      // clear out checked optional equipments, they are no longer valid
      for (const equipment of configuration.optionalEquipment) {
        equipment.implicitChanges = []
        equipment.status = 'unchecked'
        equipment.checkVersion = undefined
      }
    },
    _failConfigurationChange: (
      state,
      {
        payload: { proposal, request, process },
      }: PayloadAction<{
        proposal: ConfigurationChangeRequest
        request: ConfigurationChangeApiRequest
        process: OptionChangeId
      }>,
    ) => {
      const configuration = state.configurations[proposal.id]
      const history = configuration?.history
      if (!history || history.length <= 0) {
        return
      }
      // disallow to process the history if there are unresolved failures in it
      if (history.some((x) => x.status === 'failed-unresolved')) {
        return
      }

      const changeRequestIdx = history.findIndex((x) => x.process === process)
      // change request has not been found
      if (changeRequestIdx < 0) {
        return
      }
      const changeRequest = history[changeRequestIdx]
      // disallow non-pending failure messages
      if (changeRequest.status !== 'pending') {
        return
      }

      if ('data' in request) {
        return
      }

      history[changeRequestIdx] = {
        ...changeRequest,
        status: 'failed-unresolved',
        ended: Date.now(),
        error: makeSerializedError(request.error),
      }
    },
    applyChange: (
      state,
      { payload: { id, process } }: PayloadAction<{ id: LocalConfigurationId | undefined; process: OptionChangeRedoQueued | OptionChangeUndoQueued }>,
    ) => {
      const configuration = id && state.configurations[id]
      const history = configuration?.history
      if (!history || history.length <= 0) {
        return
      }
      history.push(process)
    },
    modifyChange: (
      state,
      {
        payload: { process, id, type },
      }: PayloadAction<{ process?: OptionChangeId; id: LocalConfigurationId | LocalConfigurationId; type: 'undo' | 'redo' }>,
    ) => {
      if ((process && !isOptionChangeSelectionId(process)) || id === undefined) {
        return
      }
      const configuration = state.configurations[id]
      const history = configuration?.history
      if (!history || history.length <= 0) {
        return
      }

      const changeRequestIdx = history.findIndex((x) => x.process === process)
      // change request hast not been found
      if (changeRequestIdx < 0) {
        return
      }
      // hh: I think this only finds the top undo entry, but it should find the next one for the current active change
      const undoIdx = history.findLastIndex((x) => x.type === 'undo' && x.sourceChange === process)
      const redoIdx = history.findLastIndex((x) => x.type === 'redo' && x.sourceChange === process)
      // there was already an undoing scheduled and it has never been re-done - or in reverse: a redo happened but no recent undo
      if ((undoIdx > redoIdx && type === 'undo') || (redoIdx > undoIdx && type === 'redo')) {
        return
      }
      const changeRequest = history[changeRequestIdx]
      // all pending or queued changes cannot be undone, yet.
      if (changeRequest.status !== 'success') {
        return
      }
      // do not allow undoing of undo or redo
      if (changeRequest.type !== 'change') {
        return
      }

      if (changeRequest.optionSource === 'ecm') {
        if (type === 'undo') {
          console.log(`ctx - undo: ${changeRequest.selections[0].fromVariant.id}`)
          history.push({
            process: makeOptionChangeId(type),
            started: Date.now(),
            config: configuration.id,
            status: 'queued',
            type,
            optionSource: changeRequest.optionSource,
            sourceChange: changeRequest.process,
            selections: changeRequest.selections,
            deselections: changeRequest.deselections,
            changes: changeRequest.consequences.changes.map((x) => ({
              ...x,
              fromVariant: x.toVariant,
              toVariant: x.fromVariant,
            })),
          })
        } else {
          // undoing change does not exists
          if (undoIdx < 0) {
            return
          }
          const undoingChange = history[undoIdx]
          // type guard, should never pass
          if (!isOptionChangeUndoId(undoingChange.process)) {
            return
          }

          console.log(`ctx - redo: ${changeRequest.selections[0].fromVariant.id}`)
          history.push({
            process: makeOptionChangeId(type),
            started: Date.now(),
            config: configuration.id,
            status: 'queued',
            type,
            optionSource: changeRequest.optionSource,
            redoingChange: undoingChange.process,
            sourceChange: changeRequest.process,
            selections: changeRequest.selections,
            deselections: changeRequest.deselections,
          })
        }
      } else if (changeRequest.optionSource === 'national-code') {
        // TODO
        if (type === 'undo') {
          history.push({
            process: makeOptionChangeId(type),
            started: Date.now(),
            ended: Date.now(),
            config: configuration.id,
            status: 'success',
            type: 'undo',
            optionSource: changeRequest.optionSource,
            selections: changeRequest.selections,
            deselections: changeRequest.deselections,
            conflicts: [],
            sourceChange: changeRequest.process,
            consequences: {
              priceDelta: -changeRequest.consequences.priceDelta,
              priceSum: changeRequest.consequences.priceSum - changeRequest.consequences.priceDelta,
              additions: [],
              changes: [],
              removals: [],
            },
            changes: changeRequest.consequences.changes.map((x) => ({
              ...x,
              fromVariant: x.toVariant,
              toVariant: x.fromVariant,
            })),
          })
        } else {
          // undoing change does not exists
          if (undoIdx < 0) {
            return
          }
          const undoingChange = history[undoIdx]
          // type guard, should never pass
          if (!isOptionChangeUndoId(undoingChange.process)) {
            return
          }

          history.push({
            process: makeOptionChangeId(type),
            started: Date.now(),
            ended: Date.now(),
            config: configuration.id,
            status: 'success',
            type: 'redo',
            optionSource: changeRequest.optionSource,
            selections: changeRequest.selections,
            deselections: changeRequest.deselections,
            conflicts: [],
            sourceChange: changeRequest.process,
            consequences: {
              priceDelta: -changeRequest.consequences.priceDelta,
              priceSum: changeRequest.consequences.priceSum - changeRequest.consequences.priceDelta,
              additions: [],
              changes: [],
              removals: [],
            },
            redoingChange: undoingChange.process,
          })
        }
      }

      // finally updating the selection, too
      for (const entry of changeRequest.selections) {
        const selection = configuration.configuredFamilies[entry.family.id]
        const selectedVariantId = type === 'undo' ? entry.fromVariant.id : entry.toVariant.id
        configuration.configuredFamilies[entry.family.id] =
          type === 'undo' && !entry.wasUserSelectedVariant
            ? undefined
            : {
                printOptions: { ...selection?.printOptions },
                isLocked: selection?.isLocked ?? false,
                selectedVariant: { id: selectedVariantId, timestamp: Date.now(), previousSelection: selection?.selectedVariant?.id },
              }
      }
    },
    clearChangeFailure: (
      state,
      {
        payload: { process, id, action },
      }: PayloadAction<{ process: OptionChangeId; id: LocalConfigurationId | undefined; action: 'ignore' | 'retry' }>,
    ) => {
      const configuration = id && state.configurations[id]
      const history = configuration?.history
      if (!history || history.length <= 0) {
        return
      }

      const changeRequestIdx = history.findIndex((x) => x.process === process)
      // change request has not been found
      if (changeRequestIdx < 0) {
        return
      }
      const changeRequest = history[changeRequestIdx]
      // disallow non-pending failure messages
      if (changeRequest.status !== 'failed-unresolved') {
        return
      }

      for (const entry of changeRequest.selections) {
        const familyId = entry.family.id
        const configuredFamily = configuration.configuredFamilies[familyId] ?? { printOptions: {}, isLocked: false }
        if (entry.wasUserSelectedVariant) {
          configuration.configuredFamilies = {
            ...configuration.configuredFamilies,
            [familyId]: { ...configuredFamily, selectedVariant: { id: entry.fromVariant.id, timestamp: Date.now() } },
          }
        } else {
          configuration.configuredFamilies = {
            ...configuration.configuredFamilies,
            [entry.family.id]: { ...configuredFamily, selectedVariant: undefined },
          }
        }
      }

      if (action === 'ignore') {
        if (changeRequest.type === 'change') {
          // resolve the failed change as a no-op change
          history[changeRequestIdx] = {
            ...changeRequest,
            status: 'failed-resolved',
          }
        } else {
          // remove the failed undo/redo from the history
          history.splice(changeRequestIdx, 1)
        }
        return
      }

      if (action === 'retry') {
        history.splice(changeRequestIdx, 1)

        if (changeRequest.type === 'change') {
          history.push({
            ...changeRequest,
            process: makeOptionChangeSelectionId(),
            started: Date.now(),
            status: 'queued',
          } satisfies OptionChangeSelectionQueued)
        } else if (changeRequest.type === 'undo') {
          history.push({
            ...changeRequest,
            process: makeOptionChangeUndoId(),
            started: Date.now(),
            status: 'queued',
          } satisfies OptionChangeUndoQueued)
        } else if (changeRequest.type === 'redo') {
          history.push({
            ...changeRequest,
            process: makeOptionChangeRedoId(),
            started: Date.now(),
            status: 'queued',
          } satisfies OptionChangeRedoQueued)
        }
      }
    },
    clearConflict: (
      state,
      {
        payload: { change: givenChange, configurationId, action },
      }: PayloadAction<{
        change: OptionChangeSucceeded
        configurationId: LocalConfigurationId | undefined
        action: 'accept' | 'undo' | 'revert'
      }>,
    ) => {
      if (!configurationId) {
        return
      }
      const configuration = configurationId && state.configurations[configurationId]
      const history = configuration?.history
      if (!configuration || !history) {
        return
      }
      const changeIdx = history.findIndex((x) => x.process === givenChange.process)
      if (changeIdx < 0) {
        return
      }
      const change = history[changeIdx]
      if (change.status !== 'success' || change.conflicts.length <= 0) {
        return
      }

      if (action === 'accept') {
        for (const conflict of change.conflicts) {
          const familyConfig = configuration.configuredFamilies[conflict.family.id]

          if (conflict.archetype === 'removal') {
            if (familyConfig) {
              familyConfig.selectedVariant = undefined
            }
            change.consequences.removals.unshift(conflict)
          }

          if (conflict.archetype === 'change') {
            if (familyConfig) {
              familyConfig.selectedVariant = {
                id: conflict.toVariant.id,
                timestamp: change.started,
                previousSelection: familyConfig?.selectedVariant?.id,
              }
            }
            change.consequences.changes.unshift(conflict)
          }
        }
        change.conflicts = []
        history[changeIdx] = change
      } else if (action === 'undo' || action === 'revert') {
        for (const entry of change.selections) {
          // revert to the prior selection, if there was any
          const explicitSelection = configuration.configuredFamilies[entry.family.id]
          if (explicitSelection) {
            explicitSelection.selectedVariant = undefined
          }
        }
        // clear the history queue
        const newHistory = history.filter((x) => x.process !== change.process && !(x.status === 'pending' || x.status === 'queued'))
        // add the undo ticket to it
        configuration.history = [
          ...newHistory,
          {
            process: makeOptionChangeUndoId(),
            started: Date.now(),
            config: configuration.id,
            status: 'queued',
            type: action,
            optionSource: change.optionSource,
            selections: change.selections,
            deselections: change.deselections,
            sourceChange: change.type !== 'change' ? change.sourceChange : change.process,
            changes: change.consequences.changes.map((x) => ({ ...x, fromVariant: x.toVariant, toVariant: x.fromVariant })),
          },
        ]
      }
    },
    // @TODO: can be removed / should be removed
    setApiConfiguration: (state, { payload }: PayloadAction<FullApiConfiguration>) => {
      state.apiConfiguration = payload
    },
    addViewHighlight: (
      state,
      {
        payload: { id, highlight, ...details },
      }: PayloadAction<
        PartialBy<TruckConfigurationHighlight, 'stage'> & {
          id: LocalConfigurationId | undefined
          /* add a highlight or overwrite all other highlights? */
          highlight?: 'additive' | 'exclusive'
        }
      >,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }

      // if the stage is not passed as parameter, insert it from the information of the configuration
      const newDetails = { stage: configuration.step.stage, ...details }
      const currentDetails = configuration.viewHighlight
      const targetScope = details.scope

      // only overwrite the panel, if it is not displaying the cosima content
      if (targetScope === 'panel' && configuration.sidepanel.current !== 'cosima') {
        configuration.sidepanel = { current: 'cosima', previous: configuration.sidepanel.current }
      }
      // there is currently no highlight in that scope
      if (!currentDetails[targetScope]) {
        configuration.viewHighlight[targetScope] = newDetails
        return
      }
      highlight = highlight ?? 'additive'
      // clear out all highlights, only keep the one
      if (highlight === 'exclusive') {
        configuration.viewHighlight = { [targetScope]: newDetails }
        return
      }
      // in additive mode, overwrite the view details for that scope (and leave the rest untouched)
      configuration.viewHighlight[targetScope] = newDetails
    },
    clearViewHighlight: (
      state,
      { payload: { id, scope } }: PayloadAction<{ id: LocalConfigurationId | undefined; scope: TruckConfigurationHighlight['scope'] }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      configuration.viewHighlight[scope] = undefined
    },
    clearAllViewHighlights: (state, { payload }: PayloadAction<LocalConfigurationId | undefined>) => {
      const configuration = payload && state.configurations[payload]
      if (!configuration) {
        return
      }
      configuration.viewHighlight = {}
    },
    _updateConfigurationCheck(
      state,
      action: PayloadAction<{
        configurationId: LocalConfigurationId | undefined
        ecmVersion: EcmConfigurationVersion
        update?: CheckResultUpdate
        finished?: boolean
        clear?: boolean
      }>,
    ) {
      const { configurationId, ecmVersion, update, finished, clear } = action.payload
      const configuration = configurationId && state.configurations[configurationId]
      if (!configuration) {
        return
      }

      let versionCheckResults = configuration.checks[ecmVersion]
      if (!versionCheckResults) {
        versionCheckResults = { ecmVersion, checks: [], status: 'queued' }
      } else if (clear) {
        versionCheckResults.checks = []
        versionCheckResults.status = 'queued'
      }

      if (versionCheckResults.status === 'success' || versionCheckResults.status === 'failure') {
        throw new Error('Cannot update a completed check')
      }

      if (update) {
        const checkIdx = versionCheckResults.checks.findIndex((x) => x.id === update.id)
        if (checkIdx < 0) {
          if (isFullCheckResult(update)) {
            versionCheckResults.checks = [...versionCheckResults.checks, update]
          } else {
            throw new Error('Cannot initiate a check with partial data')
          }
        } else {
          versionCheckResults.checks = versionCheckResults.checks.map((x) => (x.id === update.id ? { ...x, ...update } : x))
        }
      }

      if (finished) {
        versionCheckResults.status = deriveFinalStatus(versionCheckResults.checks, 'success')
      } else {
        versionCheckResults.status = 'progress'
      }

      configuration.checks = { ...configuration.checks, [ecmVersion]: versionCheckResults }
      // console.log('configuration.checks', configuration.checks)
    },
    runConfigurationChecks(
      _state,
      _action: PayloadAction<{
        id: LocalConfigurationId | undefined
        ecmVersion: EcmConfigurationVersion
        forceRetry?: boolean
      }>,
    ) {
      // nothing to do here, as the configurationCheck middleware will handle this
    },
    abortConfigurationChecks: (
      _,
      _action: PayloadAction<{
        id: LocalConfigurationId | undefined
        version: number
      }>,
    ) => {},
    setScrollPosition: (
      state,
      {
        payload: { view, id, ...scrollPosition },
      }: PayloadAction<
        Exclude<ViewDetails['scrollPosition'], undefined> & {
          view: ViewDetailKey
          id: LocalConfigurationId | undefined
        }
      >,
    ) => {
      const config = id && state.configurations[id]
      if (!config) {
        return
      }
      const viewDetails = config.viewDetails?.[view] ?? { collapsedGroups: [] }
      config.viewDetails = { ...config.viewDetails, ...{ [view]: { ...viewDetails, scrollPosition: scrollPosition } } }
    },
    clearScrollPosition: (state, { payload: { id, view } }: PayloadAction<{ view: ViewDetailKey; id: LocalConfigurationId | undefined }>) => {
      const config = id && state.configurations[id]
      if (!config?.viewDetails?.[view]) {
        return
      }

      config.viewDetails = { ...config.viewDetails, ...{ [view]: { ...config.viewDetails[view], scrollPosition: undefined } } }
    },
    setCollapsedGroups: (
      state,
      {
        payload: { view, id, collapsedGroups },
      }: PayloadAction<{ view: ViewDetailKey; id: LocalConfigurationId; collapsedGroups: EnhancedProductGroupId[] }>,
    ) => {
      const config = id && state.configurations[id]
      if (!config) {
        return
      }
      const configViewDetails = config.viewDetails ?? {}
      const viewDetails = configViewDetails[view] ?? { collapsedGroups: [] }
      config.viewDetails = { ...configViewDetails, [view]: { ...viewDetails, collapsedGroups: collapsedGroups } }
    },
    setSidepanelFocus: (
      state,
      { payload: { id, focus } }: PayloadAction<{ id: LocalConfigurationId | undefined; focus: TruckConfiguration['sidepanel']['current'] }>,
    ) => {
      const config = id && state.configurations[id]
      if (!config) {
        return
      }
      // only overwrite the sidepanel if it's not showing what is supposed to be focused
      // this is important to not have the same value for `current` and `previous`, so the closing of a panel works correctly
      if (config.sidepanel.current !== focus) {
        config.sidepanel = { current: focus, previous: config.sidepanel.current }
      }
    },
    _setConfigurationTimer: (
      state,
      { payload: { id, remainingTimeInMs } }: PayloadAction<{ id: LocalConfigurationId | undefined; remainingTimeInMs: number }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      configuration.lastApiInteraction = (Date.now() - state.meta.sessionExpiryInMs + remainingTimeInMs) as Timestamp
    },
    takeSnapshot: (
      state,
      { payload: { id, ...partialSnapshot } }: PayloadAction<Omit<ConfigurationSnapshot, 'id' | 'when'> & { id: LocalConfigurationId | undefined }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      configuration.snapshots.push({
        ...partialSnapshot,
        when: Date.now(),
        id: crypto.randomUUID() as SnapshotId,
      })
    },
    clearRecoverableConfiguration: (state, { payload }: PayloadAction<LocalConfigurationId>) => {
      state.recoverableConfigurations = state.recoverableConfigurations.filter((x) => x.id !== payload)
    },
    checkGroupCompatibility: (state, { payload: { id, groupId } }: PayloadAction<{ id?: LocalConfigurationId; groupId: EnhancedProductGroupId }>) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }

      trackVariantCompatibility(configuration, configuration.version, groupId)
    },
    clearCompatibilityRequests: (
      state,
      { payload: { id, version, groupId } }: PayloadAction<{ id?: LocalConfigurationId; groupId: EnhancedProductGroupId; version: number }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }

      if (!configuration.compatibility || configuration.compatibility.version !== version) {
        return
      }

      configuration.compatibility.requestedProductGroups = configuration.compatibility.requestedProductGroups.filter((id) => id !== groupId)
    },
    addCompatibilityEntries: (
      state,
      {
        payload: { id, version, checkedProductGroup },
      }: PayloadAction<{
        id?: LocalConfigurationId
        version: number
        checkedProductGroup: EnhancedProductGroupId
      }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration || configuration.version > version) {
        return
      }
      trackVariantCompatibility(configuration, configuration.version, checkedProductGroup)
    },
    _addTechnicalData: (
      state,
      {
        payload: { id, technicalData },
      }: PayloadAction<{
        id: LocalConfigurationId
        technicalData: Exclude<TruckConfiguration['technicalData'], undefined>
      }>,
    ) => {
      const configuration = state.configurations[id]
      // ignore insertions of technical data that's older than the current version
      if (!configuration || configuration.version > technicalData.version) {
        return
      }
      configuration.technicalData = technicalData
    },
    loadTechnicalData: (_state, _action: PayloadAction<LocalConfigurationId>) => {
      // virtual, does nothing and is handled by a middleware
    },
    addNewOptionalEquipmentPackage: (
      state,
      { payload: { id, equipmentId } }: PayloadAction<{ id: LocalConfigurationId | undefined; equipmentId: ConfigurationOptionalEquipmentId }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      configuration.optionalEquipment.push({
        id: equipmentId,
        title: '',
        variants: [],
        implicitChanges: [],
        status: 'unchecked',
      })
      updateVersion(configuration, false)
    },
    updateOptionalEquipmentTitle: (
      state,
      {
        payload: { id, equipmentId, title, description },
      }: PayloadAction<{
        id: LocalConfigurationId | undefined
        equipmentId: ConfigurationOptionalEquipmentId
        title: string
        description: string | undefined
      }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      const entry = configuration.optionalEquipment.find((x) => x.id === equipmentId)
      if (!entry) {
        return
      }
      entry.title = title
      entry.description = description
      updateVersion(configuration, false)
    },
    setEquipmentStatus: (
      state,
      {
        payload: { id, equipment: equipmentId, status },
      }: PayloadAction<{
        id: LocalConfigurationId | undefined
        equipment: ConfigurationOptionalEquipmentId
        status: ConfigurationOptionalEquipment['status']
      }>,
    ) => {
      const configuration = id && state.configurations[id]
      const equipment = configuration?.optionalEquipment.find((x) => x.id === equipmentId)
      if (!equipment || !configuration) {
        return
      }
      equipment.status = status
      updateVersion(configuration, false)
    },
    addEquipmentVariant: (
      state,
      {
        payload: { id, equipment, family, targetVariant, sourceVariant, version },
      }: PayloadAction<{
        id: LocalConfigurationId | undefined
        equipment: ConfigurationOptionalEquipmentId
        family: ConfigurationOptionalEquipment['variants'][number]['family']
        sourceVariant: ConfigurationOptionalEquipment['variants'][number]['sourceVariant']
        targetVariant: ConfigurationOptionalEquipment['variants'][number]['targetVariant']
        version: number
      }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      const entry = configuration?.optionalEquipment.find((x) => x.id === equipment)
      if (!entry) {
        return
      }
      updateVersion(configuration, false)
      // every time something is added, the status falls back to unchecked
      entry.status = 'unchecked'
      entry.implicitChanges = []
      const existingIndex = entry.variants.findIndex((x) => x.family.id === family.id)
      if (existingIndex >= 0) {
        const variantEntry = entry.variants[existingIndex]
        variantEntry.targetVariant = targetVariant
        variantEntry.sourceVariant = sourceVariant
        return
      }

      entry.variants.push({
        family,
        sourceVariant,
        targetVariant,
        version,
      })
    },
    removeEquipmentVariant: (
      state,
      {
        payload: { id, equipment, family, targetVariant },
      }: PayloadAction<{
        id: LocalConfigurationId | undefined
        equipment: ConfigurationOptionalEquipmentId
        family: ConfigurationOptionalEquipment['variants'][number]['family']['id']
        targetVariant: ConfigurationOptionalEquipment['variants'][number]['targetVariant']['id']
      }>,
    ) => {
      const configuration = id && state.configurations[id]
      if (!configuration) {
        return
      }
      const entry = configuration?.optionalEquipment.find((x) => x.id === equipment)
      if (!entry) {
        return
      }
      const existingIndex = entry.variants.findIndex((x) => x.family.id === family && x.targetVariant.id === targetVariant)
      if (existingIndex < 0) {
        return
      }
      // every time something is removed, the status falls back to unchecked
      entry.status = 'unchecked'
      entry.variants = entry.variants.filter((_x, i) => i !== existingIndex)
      entry.implicitChanges = []
      updateVersion(configuration, false)
    },
    replaceEquipmentImplicitVariants: (
      state,
      {
        payload: { id, equipment: equipmentId, variants },
      }: PayloadAction<{
        id: LocalConfigurationId | undefined
        equipment: ConfigurationOptionalEquipmentId
        variants: ConfigurationOptionalEquipment['implicitChanges']
      }>,
    ) => {
      const configuration = id && state.configurations[id]
      const equipment = configuration?.optionalEquipment.find((x) => x.id === equipmentId)
      if (!configuration || !equipment) {
        return
      }
      equipment.implicitChanges = [...variants]
      updateVersion(configuration, false)
    },
    duplicateEquipment: (
      state,
      {
        payload: { id, equipment: equipmentId },
      }: PayloadAction<{ id: LocalConfigurationId | undefined; equipment: ConfigurationOptionalEquipmentId }>,
    ) => {
      const configuration = id && state.configurations[id]
      const equipment = configuration?.optionalEquipment.find((x) => x.id === equipmentId)
      if (!configuration || !equipment) {
        return
      }
      const newId = createOptionalEquipmentId()
      configuration.optionalEquipment.push({
        ...equipment,
        title: '',
        description: undefined,
        id: newId,
        status: 'unchecked',
        implicitChanges: [],
      })
      updateVersion(configuration, false)
    },
    removeEquipment: (
      state,
      {
        payload: { id, equipment: equipmentId },
      }: PayloadAction<{ id: LocalConfigurationId | undefined; equipment: ConfigurationOptionalEquipmentId }>,
    ) => {
      const configuration = id && state.configurations[id]
      const equipment = configuration?.optionalEquipment.find((x) => x.id === equipmentId)
      if (!configuration || !equipment) {
        return
      }
      equipment.status = 'removed'
      updateVersion(configuration, false)
    },
    settleTg3Conversion: (
      state,
      { payload: { configurationId, step } }: PayloadAction<{ configurationId: LocalConfigurationId | undefined; step: ConfigurationStep }>,
    ) => {
      const configuration = configurationId && state.configurations[configurationId]
      if (!configuration) {
        return
      }
      const settledSteps = configuration.tg3?.appliedConversion ?? []
      if (settledSteps.includes(step)) {
        return
      }
      configuration.tg3 = {
        ...configuration.tg3,
        appliedConversion: [...settledSteps, step],
      }
    },
    setLeadingConfiguration: (state, { payload }: PayloadAction<LocalConfigurationId | undefined>) => {
      const configuration = payload && state.configurations[payload]
      if (!configuration) {
        return
      }
      state.leadingConfiguration = payload
    },
    ...marketDataReducers,
  },
  extraReducers: (builder) => {
    // addConfigChangeTracker(builder)
    addConflictErrorTracker(builder)
  },
})

export const {
  createNewConfiguration,
  setView,
  setVariantsForFamily,
  setVariantForNationalCodeFamily,
  setPrintOptions,
  toggleLocked,
  clearChangeFailure,
  clearConflict,
  setApiConfiguration,
  setIsManCodeSaving,
  setManCode,
  setConfigurationTitle,
  closeConfiguration,
  addViewHighlight,
  clearViewHighlight,
  clearAllViewHighlights,
  addPendingConfiguration,
  // removePendingConfiguration,
  _updateConfigurationCheck,
  runConfigurationChecks,
  abortConfigurationChecks,
  setApiMode,
  setScrollPosition,
  clearScrollPosition,
  setCollapsedGroups,
  applyConfigurationBaseParameterChange,
  applyChange,
  modifyChange,
  setSidepanelFocus,
  _setConfigurationTimer,
  takeSnapshot,
  clearRecoverableConfiguration,
  checkGroupCompatibility,
  clearCompatibilityRequests,
  addCompatibilityEntries,
  _setEcmState,
  _randomizePrices,
  loadTechnicalData,
  addNewOptionalEquipmentPackage,
  updateOptionalEquipmentTitle,
  setEquipmentStatus,
  addEquipmentVariant,
  removeEquipmentVariant,
  replaceEquipmentImplicitVariants,
  duplicateEquipment,
  removeEquipment,
  settleTg3Conversion,

  // from market slice
  addMarketDetailsAndAttributes,
  addMarketAttributes,
  addMarketDetails,
  setLeadingConfiguration,
} = truckConfigurationSlice.actions

const allActionsSet = new Set<string>(Object.values(truckConfigurationSlice.actions).map((action) => action.type))
export function isConfigurationAction(action: UnknownAction): boolean {
  return allActionsSet.has(action.type)
}

export const truckConfigurationActions = truckConfigurationSlice.actions
